<template>
  <section
    class="popup"
    :class="{
      'full-height': fullHeight,
      transparent
    }"
  >
    <div
      class="popup-bg"
      @click="closePopup($event, false)"
    />
    <div
      class="popup-inner"
      :style="popupInnerStyle"
    >
      <button
        v-if="type !== 'intro' && !disableClose"
        :aria-label="$t('close')"
        class="popup-mobile-close"
        @click="closePopup($event, false)"
      >
        <icon class="icon-close-info-white" />
      </button>
      <div
        class="popup-iframe-container"
        :class="{ 'fixed-height': type === 'intro', 'bg-white': isIframe }"
        :style="popupContainerHeight"
      >
        <div class="info">
          <i-favorite
            v-if="favoritesMarkerTypes.includes(marker.type)"
            category="marker"
            :item="marker"
          />
          <i-tag-list :tags="markerCategory" />
        </div>
        <a
          v-if="type !== 'intro' && !disableClose"
          href="javascript:"
          @click="closePopup($event, false)"
        >
          <span class="popup-close" />
        </a>
        <div class="wrapper">
          <video
            v-if="isVideo"
            ref="video"
            onloadstart="this.volume = 0.6"
            controlslist="nodownload"
            :muted="type === 'intro'"
            playsinline
            autoplay
            controls
            disablepictureinpicture
            disableremoteplayback
            x-webkit-airplay="deny"
            @play="videoPlayed = true"
            @ended="closePopup($event, true)"
            @contextmenu.prevent
          >
            <source
              type="video/mp4"
              :src="url"
            >
          </video>
          <div
            v-else-if="isImage"
            class="image"
            :style="{ backgroundImage: `url('${url}')` }"
          />
          <div
            v-else
            class="iframe-wrapper"
          >
            <iframe
              ref="iframe"
              class="embed-responsive-item"
              :src="iframeUrl"
              frameborder="0"
              allowfullscreen
              :allow="iframeAttrAllow"
              @load="onIframeLoad"
            >
              {{ $t('urlNoSupportIFrameError') }}
            </iframe>
            <button
              v-if="showIosUnmuteTip"
              ref="iosAudioToggle"
              type="button"
              class="ios-unmute-tip"
              @click="handleIosUnmute"
            >
              {{ iosAudioButtonText }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="type === 'intro'"
      class="popup-action"
    >
      <button
        :class="{ active: showAction }"
        @click="closePopup($event, true)"
      >
        {{ actionText }}
      </button>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import {
  POPUP,
  INTRO_TYPE
} from '~js/constants'
import Icon from '@/components/Icon/index'
import IFavorite from '@/components/IFavorite'
import ITagList from '@/components/ITagList'
import { ARVIEWER_QUERIES } from '@/api/armaker'

export default {
  name: 'PopupIndex',
  components: {
    Icon,
    IFavorite,
    ITagList
  },
  props: {
    url: {
      type: String,
      default () {
        return ''
      }
    },
    type: {
      type: String,
      default () {
        return POPUP.TYPE
      }
    },
    fullHeight: {
      type: Boolean,
      default () {
        return POPUP.FULL_HEIGHT
      }
    },
    widthPercent: {
      type: Number,
      default () {
        return POPUP.WIDTH_PERCENT
      }
    },
    marker: {
      type: Object,
      default () {
        return {}
      }
    },
    disableClose: {
      type: Boolean,
      default: false
    },
    transparent: {
      type: Boolean,
      default: false
    },
    introAssetSourceType: {
      type: String,
      default: INTRO_TYPE.BUILDING
    }
  },
  data: () => ({
    videoPlayed: false,
    showAction: false,
    timeout: null,
    INTRO_TYPE,
    isIosDevice: false,
    showIosUnmuteTip: false,
    isIosAudioMuted: true
  }),
  computed: {
    ...mapState('favorites', {
      favoritesMarkerTypes: state => state.acceptedMarkerTypes
    }),
    ...mapGetters([
      'currentBuilding'
    ]),
    introAsset () {
      switch (this.introAssetSourceType) {
        case INTRO_TYPE.BUILDING:
          return this.currentBuilding.introAssetsAttribute
        case INTRO_TYPE.TOURRING:
          return this.currentBuilding.meta && this.currentBuilding.meta.tourringIntroAssetsAttribute ? this.currentBuilding.meta.tourringIntroAssetsAttribute : {}
        default:
          return {}
      }
    },
    isVideo () {
      return this.url.includes('.mp4') || (this.type === 'intro' && this.introAsset.type === 'video')
    },
    isImage () {
      return this.type === 'intro' && this.introAsset.type === 'image'
    },
    isIframe () {
      return !this.isImage && !this.isVideo
    },
    isYouTubeIframe () {
      return /youtube(?:-nocookie)?\.com\/embed|youtu\.be/.test(this.url)
    },
    shouldUseIosYoutubeWorkaround () {
      return this.isIosDevice && this.isYouTubeIframe
    },
    isTransparentArMarkerTag () {
      const isArMarker = this.marker.type === 'arMakerTag'
      const disableBackground = this.marker.customAttribute
        ? this.marker.customAttribute.disableBackground
        : false
      return isArMarker && disableBackground
    },
    iframeUrl () {
      let result = this.url
      if (this.isTransparentArMarkerTag) {
        const [key, value = ''] = ARVIEWER_QUERIES.noBackground.split('=')
        result = this.ensureQueryParam(result, key, value)
      }
      if (this.shouldUseIosYoutubeWorkaround) {
        result = this.ensureQueryParam(result, 'autoplay', '1')
        result = this.ensureQueryParam(result, 'mute', '1')
        result = this.ensureQueryParam(result, 'playsinline', '1')
        result = this.ensureQueryParam(result, 'enablejsapi', '1')
      }
      return result
    },
    popupInnerStyle () {
      return {
        width: `${this.widthPercent || POPUP.WIDTH_PERCENT}%`
      }
    },
    popupContainerHeight () {
      return {
        height: window.innerWidth * (this.widthPercent / 100 * 9 / 16) + 'px'
      }
    },
    iframeAttrAllow () {
      if (this.shouldUseIosYoutubeWorkaround || this.url.includes('autoplay=1')) {
        return 'camera;microphone;autoplay;encrypted-media;fullscreen;picture-in-picture'
      }
      return 'camera;microphone'
    },
    actionText () {
      return this.introAsset.buttonText || this.$t('introAssets.enter')
    },
    introDuration () {
      return isNaN(Number(this.introAsset.duration)) ? 3 : Number(this.introAsset.duration)
    },
    markerCategory () {
      return this.marker?.categoryMulti?.[this.$i18n.locale] || this.marker?.category
    },
    iosAudioButtonText () {
      const text = this.isIosAudioMuted ? this.$t('popup.soundOn') : this.$t('popup.soundOff')
      const icon = this.isIosAudioMuted ? '🔈 ' : '🔇 '
      return `${icon}${text}`
    }
  },
  watch: {
    shouldUseIosYoutubeWorkaround () {
      this.$nextTick(() => {
        this.setupIosYoutubeWorkaround()
      })
    },
    iframeUrl () {
      this.$nextTick(() => {
        this.setupIosYoutubeWorkaround()
      })
    }
  },
  mounted () {
    window.addEventListener('click', this.clickHandler)
    this.isIosDevice = this.detectIosDevice()
    this.$nextTick(() => {
      this.setupIosYoutubeWorkaround()
    })
    if (this.type === 'intro' && this.introAssetSourceType === INTRO_TYPE.BUILDING) {
      this.startTimer()
    }
  },
  beforeDestroy () {
    window.removeEventListener('click', this.clickHandler)
    clearTimeout(this.timeout)
    this.removeIosListeners()
  },
  methods: {
    ...mapActions([
      'setPopupMarker'
    ]),
    detectIosDevice () {
      if (typeof navigator === 'undefined') {
        return false
      }
      const ua = navigator.userAgent || ''
      const iOS = /iP(ad|hone|od)/.test(ua)
      const touchMac = /Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document
      return iOS || touchMac
    },
    setupIosYoutubeWorkaround () {
      this.removeIosListeners()
      if (!this.shouldUseIosYoutubeWorkaround) {
        this.showIosUnmuteTip = false
        return
      }
      this.showIosUnmuteTip = true
      this.isIosAudioMuted = true
      if (typeof window !== 'undefined') {
        window.addEventListener('pointerdown', this.handleIosUnmute, { once: true })
        window.addEventListener('keydown', this.handleIosUnmute, { once: true })
      }
    },
    removeIosListeners () {
      if (typeof window === 'undefined') {
        return
      }
      window.removeEventListener('pointerdown', this.handleIosUnmute)
      window.removeEventListener('keydown', this.handleIosUnmute)
    },
    ensureQueryParam (input, key, value) {
      if (!key) {
        return input
      }
      const hasQuery = input.includes('?')
      const pattern = new RegExp(`([?&])${key}=.*?(&|#|$)`, 'i')
      if (pattern.test(input)) {
        return input.replace(pattern, `$1${key}=${value}$2`)
      }
      const separator = hasQuery ? '&' : '?'
      return `${input}${separator}${key}${value !== undefined ? `=${value}` : ''}`
    },
    clickHandler (e) {
      if (!this.videoPlayed) {
        this.$refs.video && this.$refs.video.play()
      }
    },
    onIframeLoad () {
      if (this.shouldUseIosYoutubeWorkaround) {
        this.sendYoutubeCommand('playVideo')
      }
    },
    handleIosUnmute (event) {
      if (!this.shouldUseIosYoutubeWorkaround) {
        return
      }

      const toggleButton = this.$refs.iosAudioToggle
      const fromButton = toggleButton && event && toggleButton.contains(event.target)
      const isPointerEvent = event && event.type === 'pointerdown'
      const isKeydownEvent = event && event.type === 'keydown'

      if ((isPointerEvent || isKeydownEvent) && !fromButton) {
        this.removeIosListeners()
      }

      if (fromButton && (isPointerEvent || isKeydownEvent)) {
        return
      }

      if (this.isIosAudioMuted) {
        this.isIosAudioMuted = false
        this.removeIosListeners()
        this.sendYoutubeCommand('unMute')
        this.sendYoutubeCommand('playVideo')
        return
      }

      this.isIosAudioMuted = true
      this.sendYoutubeCommand('mute')
    },
    sendYoutubeCommand (func, args = []) {
      const iframe = this.$refs.iframe
      if (!iframe || !iframe.contentWindow) {
        return
      }
      iframe.contentWindow.postMessage(JSON.stringify({
        event: 'command',
        func,
        args
      }), '*')
    },
    startTimer () {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.showAction = true
      }, Math.abs(this.introDuration * 1000))
    },
    closePopup (e, force = false) {
      if (!force && (this.type === 'intro' || this.disableClose)) {
        return
      }
      if (this.introAssetSourceType === INTRO_TYPE.TOURRING) {
        if (this.$refs.video) {
          this.$refs.video.currentTime = 0
          this.$refs.video.play()
        }
        return
      }
      this.setPopupMarker({})
      this.$emit('closePopup')
    }
  }
}
</script>

<style lang="stylus" rel="stylesheet/stylus" scoped>
@import '~css/addon/main'
@import '~css/common/extends'

.popup {
  @extend .absolute-full
  display: flex
  flex-direction: column
  z-index: $popup-z
}
.transparent {
  .popup-bg,
  .popup-inner,
  .popup-iframe-container {
    background: transparent !important
  }
}

.popup-bg {
  @extend .absolute-full
  background-color: alpha($black, 70%)
}

.popup-inner {
  @extend .flex-center-column
  margin: 0 auto
  position: relative
  min-width: 320px
  width: 65%
  height: 100%
  flex: 1
  z-index: 1

  .info {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 2;
    align-self: start
    display: flex
    align-items: center
    margin-bottom: 10px

    .i-favorite {
      margin-right: 10px
    }
    .i-tag-list /deep/ .tag {
      margin-bottom: 0
    }
  }
}

.popup-action {
  flex-basis: 80px
  display: flex
  justify-content: center
  align-items: center
  padding: 8px 8px 20px
  z-index: 1

  button {
    padding: 10px 20px
    color: #FFF
    background: transparent
    font-size: 18px
    line-height: 26px
    font-weight: bold
    border: 1px solid #FFF
    border-radius: 24px // (10*2 + 26 + 1*2) / 2
    opacity: 0
    visibility: hidden
    transition: opacity .2s linear
    user-select: none

    &:hover {
      background: rgba(#FFF, .3)
    }
    &.active {
      opacity: 1
      visibility: visible
    }
  }
}

.popup-iframe-container {
  position: relative
  width: 100%
  background-color: #000

  &.fixed-height {
    height: 100% !important
    padding-bottom: 0 !important
  }
}

.popup-iframe-container.bg-white {
  background-color: #fff
}

.wrapper {
  @extend .absolute-full
  @extend .overflow-auto

  > * {
    @extend .absolute-full
    border: 0
  }
  .image {
    background: center/contain no-repeat
  }
}

.iframe-wrapper {
  position: relative
}

.iframe-wrapper .embed-responsive-item {
  @extend .absolute-full
  border: 0
}

.ios-unmute-tip {
  position: absolute
  right: 16px
  bottom: 16px
  z-index: 2
  padding: 10px 14px
  border: 0
  border-radius: 999px
  cursor: pointer
  background: rgba(#000, .6)
  color: #fff
  font-size: 14px
  line-height: 1.2
}

.popup-close {
  bg-size(34px, 34px)
  position: absolute
  z-index: $popup-close-z
  right: 0px
  top: 0px
  background-position: center center
  background-image: url('./img/close-popup-square.png')
  display: none
}

.popup-mobile-close {
  @extend .flex-center
  @extend .btn-no-default
  position: fixed
  right: $viewer-layer-viewer-layer-close
  top: 7px
  padding: 10px
  z-index: $viewer-layer-close-z
  display: none

  .icon-close-info-white {
    $w = 20px
    $h = 20px
    bg-size($w, $h)
  }
}

.popup-iframe-hint {
  @extend .absolute-full
  @extend .flex-center
  text-shadow: none
}

@media (orientation: landscape) {
  .popup-close {
    display: block
  }
  .popup.full-height .popup-iframe-container {
    height: 100% !important;
  }
}
@media (orientation: portrait) {
  .popup-inner {
    background-color: rgba(88, 88, 90, 0.9)
  }
  .popup-iframe-container {
    width: 100%
    height: 100% !important
  }
  .popup-mobile-close {
    display: block
  }
}
</style>
